import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { take } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { CONFIRMATION_TYPES } from '../../enums/confirmation-types.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MaterialService } from '../../services/material/material.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { NotificationService } from '../../services/notification/notification.service';
import { TimeRecordingService } from '../../services/time-recording/time-recording.service';
import { TimesheetAndExpensesService } from '../../services/timesheet/timesheet-and-expenses.service';
import { UserMasterService } from '../../services/user-master/user-master.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { StatusModel } from '../low-level/status.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { DurationModel } from '../primitive/duration.model';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import {
    ConfirmationAdditionalEmailsModel,
    ConfirmationAdditionalEmailsModelFromJson,
    JsonConfirmationAdditionalEmailsModel,
} from './confirmation-additional-emails.model';
import {
    ConfirmationAttendeesModel,
    ConfirmationAttendeesModelFromJson,
    JsonConfirmationAttendeesModel,
} from './confirmation-attendees.model';
import {
    ConfirmationCommentsForCustomerModel,
    ConfirmationCommentsForCustomerModelFromJson,
    JsonConfirmationCommentsForCustomerModel,
} from './confirmation-comments-for-customer.model';
import {
    ConfirmationComplaintsModelFromJson,
    ConfirmationComplaintsP58Model,
    JsonConfirmationComplaintsModel,
    ODataConfirmationComplaintsOutModel,
} from './confirmation-complaints-p58.model';
import {
    ConfirmationEquipmentModel,
    ConfirmationEquipmentModelFromJson,
    JsonConfirmationEquipmentModel,
    ODataConfirmationEquipmentOutModel,
} from './confirmation-equipment.model';
import {
    ConfirmationEventSummaryModel,
    ConfirmationEventSummaryModelFromJson,
    JsonConfirmationEventSummaryModel,
} from './confirmation-event-summary.model';
import {
    ConfirmationExpensesModel,
    ConfirmationExpensesModelFromJson,
    JsonConfirmationExpensesModel,
    ODataConfirmationExpensesOutModel,
} from './confirmation-expenses.model';
import {
    ConfirmationFollowupModel,
    ConfirmationFollowupModelFromJson,
    JsonConfirmationFollowupModel,
    ODataConfirmationFollowupOutModel,
} from './confirmation-followup.model';
import {
    ConfirmationInterruptionsModel,
    ConfirmationInterruptionsModelFromJson,
    JsonConfirmationInterruptionsModel,
} from './confirmation-interruptions.model';
import {
    ConfirmationMaterialConsumeModel,
    ODataConfirmationMaterialConsumeOutModel,
} from './confirmation-material-consume.model';
import {
    ConfirmationMeasurementToolsModel,
    ConfirmationMeasurementToolsModelFromJson,
    JsonConfirmationMeasurementToolsModel,
    ODataConfirmationMeasurementToolsOutModel,
} from './confirmation-measurement-tools.model';
import {
    ConfirmationMeasuringPointsModel,
    ConfirmationMeasuringPointsModelFromJson,
    JsonConfirmationMeasuringPointsModel,
    ODataConfirmationMeasuringPointsOutModel,
} from './confirmation-measuring-points.model';
import {
    ConfirmationParticipantsModel,
    ConfirmationParticipantsModelFromJson,
    JsonConfirmationParticipantsModel,
    ODataConfirmationParticipantsOutModel,
} from './confirmation-participants.model';
import {
    ConfirmationReportTextModel,
    ConfirmationReportTextModelFromJson,
    JsonConfirmationReportTextModel,
} from './confirmation-report-text.model';
import {
    ConfirmationServiceReportModel,
    ConfirmationServiceReportModelFromJson,
    JsonConfirmationServiceReportModel,
    ODataConfirmationServiceReportOutModel,
} from './confirmation-service-report.model';
import { ConfirmationSummaryModel, ODataConfirmationSummaryOutModel } from './confirmation-summary.model';
import {
    ConfirmationTimeRecordingP40Model,
    ConfirmationTimeRecordingP40ModelFromJson,
    JsonConfirmationTimeRecordingP40Model,
} from './confirmation-time-recording-p40.model';
import {
    ConfirmationTimeRecordingModel,
    ConfirmationTimeRecordingModelFromJson,
    JsonConfirmationTimeRecordingModel,
    ODataConfirmationTimeRecordingOutModel,
} from './confirmation-time-recording.model';
import {
    ConfirmationTrainingEventCompletedModel,
    ConfirmationTrainingEventCompletedModelFromJson,
    JsonConfirmationTrainingEventCompletedModel,
} from './confirmation-training-event-completed.model';
import {
    ConfirmationUploadToDocboxModel,
    ConfirmationUploadToDocboxModelFromJson,
    JsonConfirmationUploadToDocboxModel,
} from './confirmation-upload-to-docbox.model';
import { ODataTimeZoneOutModel } from './time-zone.model';
import { ConfirmationReasonMissingProtocolModel } from './confirmation-reason-missing-protocol.model';
import {
    ConfirmationSourceReplacementsModel,
    ConfirmationSourceReplacementsModelFromJson,
    JsonConfirmationSourceReplacementsModel,
} from './confirmation-source-replacements.model';

/**
 * Custom merge function for use with _.mergeWith. Extends existing arrays with new entries instead of
 * overwriting the entire array when merging an array property into an object.
 *
 * @param objValue the value of the property in the object being merged into
 * @param srcValue the value of the property in the object being merged from
 * @returns the merged value or undefined if no merging should be done
 */
function mergeCustomizer(objValue: unknown, srcValue: unknown): unknown[] | undefined {
    if (Array.isArray(objValue)) {
        return objValue.concat(srcValue);
    }
}

export class ConfirmationModel extends CompositeModel {
    protected log: LogInterface;
    protected translate: TranslateService;
    protected configService: ConfigService;
    protected modelOptionsLoader: ModelOptionsLoaderService;
    protected notificationService: NotificationService;
    protected valueHelperService: ValueHelperService;
    protected materialService: MaterialService;
    protected notificationUtilities: NotificationUtilities;
    protected timeRecordingService: TimeRecordingService;
    protected timesheetAndExpensesService: TimesheetAndExpensesService;
    protected userMasterService: UserMasterService;
    protected clientManager: ClientManagerInterface;

    public equipment: ConfirmationEquipmentModel | undefined;
    public materials: ConfirmationMaterialConsumeModel | undefined;
    public measurementTool: ConfirmationMeasurementToolsModel | undefined;
    public measuringPoints: ConfirmationMeasuringPointsModel;
    public timeRecording: ConfirmationTimeRecordingModel;
    public expenses: ConfirmationExpensesModel | undefined;
    public uploadToDocBox: ConfirmationUploadToDocboxModel | undefined;
    public participants: ConfirmationParticipantsModel;
    public sourceReplacements: ConfirmationSourceReplacementsModel | undefined;
    public serviceReport: ConfirmationServiceReportModel | undefined;
    public complaints: ConfirmationComplaintsP58Model | undefined;
    public followup: ConfirmationFollowupModel | undefined;
    public attendees: ConfirmationAttendeesModel | undefined;
    public interruptions: ConfirmationInterruptionsModel | undefined;
    public additionalEmails: ConfirmationAdditionalEmailsModel | undefined;
    public eventSummary: ConfirmationEventSummaryModel | undefined;
    public timeRecordingP40: ConfirmationTimeRecordingP40Model | undefined;
    public reportText: ConfirmationReportTextModel | undefined;
    public commentsForCustomer: ConfirmationCommentsForCustomerModel | undefined;
    public trainingEventCompleted: ConfirmationTrainingEventCompletedModel | undefined;
    public reasonMissingProtocols: ConfirmationReasonMissingProtocolModel | undefined;
    public summary: ConfirmationSummaryModel;
    public utcDiff: DurationModel;
    public utcSign: TextModel;
    public timeZone: TextModel;
    public jobRole: TextModel;
    public shortConfirmationIdent: TextModel;
    public salesOrg: TextModel;
    public plant: TextModel;
    public submissionDateTime: TextModel;
    public notificationCurrentStatus: StatusModel;
    public isNotificationRemoteWork: boolean;

    // Below properties are to store the information that whether an accordion was opened or not
    // It is used for displaying appropriate status icons for respective sections
    public accordionOpenedStatus: AccordionOpenedStatus = {
        equipment: false,
        complaints: false,
        complaintsAA: false,
        materials: false,
        measurementTool: false,
        measuringPoints: false,
        serviceReport: false,
        timeRecording: false,
        expenses: false,
        uploadToDocBox: false,
        participants: false,
        sourceReplacements: false,
        followup: false,
        timeRecordingP40: false,
        attendees: false,
        interruptions: false,
        additionalEmails: false,
        eventSummary: false,
        reportText: false,
        commentsForCustomer: false,
        trainingEventCompleted: false,
    };

    // This flag is used to store if the confirmation has been sent to the backend already as leaving the confirmation
    // screen after submitting is calling the ConfirmationCanDeactivateGuard, which is storing the confirmation again
    // in localStorage/IndexDB after it has been submitted and deleted from there. We use it to not store it again
    // after successful submission. It will be set in ConfirmationService.submitConfirmation only.
    // There is no need to write it out to localStorage/IndexDB in toJSON as the ConfirmationCanDeactivateGuard works
    // with the in-memory version of the confirmation from ConfirmationService.
    public isSubmitted: boolean;

    // Timestamp when the model is writing to storage (IndexedDB, Concurrent Usage Store, etc.) as UTC milliseconds
    // from Date.now() or moment().utc().valueOf() or moment().valueOf().
    public saveTimestamp: number;
    public constructor(
        public notificationIdentifier: NotificationIdentifierModel,
        public type: CONFIRMATION_TYPES,
    ) {
        super('ConfirmationModel', {});

        // Dependency injection
        const injector = CustomInjector.getInjector();
        this.log = injector.get(LogInstanceToken);
        this.translate = injector.get(TranslateService);
        this.configService = injector.get(ConfigService);
        this.modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.notificationService = injector.get(NotificationService);
        this.valueHelperService = injector.get(ValueHelperService);
        this.materialService = injector.get(MaterialService);
        this.notificationUtilities = injector.get(NotificationUtilities);
        this.timeRecordingService = injector.get(TimeRecordingService);
        this.timesheetAndExpensesService = injector.get(TimesheetAndExpensesService);
        this.userMasterService = injector.get(UserMasterService);
        this.clientManager = injector.get(ClientManagerInstanceToken);

        // make sure start-of-work is set. Usually this would be ensured by the travel-status-update, but if the app
        // was deleted after sending OSR and then reinstalled we could end up in a confirmation without start-of-work
        // having been set. Since we will access it when doing the time-recording, this is the last place we can make
        // sure it is set.
        const currentTimesheet = this.timesheetAndExpensesService.getCurrentTimesheet();
        if (!currentTimesheet.startOfWork.isValid()) {
            // set start-of-work to now. Since we assume localStorage was deleted, there is no use in checking for
            // other, earlier notifications here
            this.timesheetAndExpensesService.saveStartOfWork(moment().startOf('minute')).pipe(take(1)).subscribe();
        }

        const notificationModel = this.notificationService.getNotificationByIdentifier(this.notificationIdentifier);

        if (!notificationModel) {
            throw new Error(`Could not load notification ${this.notificationIdentifier} for confirmation model!`);
        }
        this.isNotificationRemoteWork = this.notificationService.isNotificationRemoteWork(notificationModel);

        if (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) {
            this.notificationCurrentStatus = this.notificationService.getStatus(notificationModel);
            const timeRecordingBillableCodes = this.valueHelperService.getTimeRecordingBillableCodes();
            const timeRecordingTaskTypeInfoCodes = this.clientManager.isNgmAP0()
                ? this.valueHelperService.getCustomTimeRecordingTasksForAP0(notificationModel)
                : this.configService.getEnableRemoteWork()
                  ? this.valueHelperService.getRemoteWorkTimeRecordingTaskTypeInfo(this.notificationCurrentStatus)
                  : this.valueHelperService.getTimeRecordingTaskTypeInfo();
            const timeRecordingDestinationCodes = this.valueHelperService.getTravelDestination();
            const timeRecordingMeansOfTransportationCodes = this.valueHelperService.getMeansOfTransportation();
            const phoneFixIndicatorAA =
                this.clientManager.isNgmAA() &&
                notificationModel.notificationType === ConfigConstants.NotificationTypes.Service &&
                this.valueHelperService.getPhoneFixIndicator();
            this.timeRecording = new ConfirmationTimeRecordingModel(
                notificationModel,
                timeRecordingBillableCodes,
                timeRecordingTaskTypeInfoCodes,
                timeRecordingMeansOfTransportationCodes,
                timeRecordingDestinationCodes,
                phoneFixIndicatorAA,
            );
            this.accordionOpenedStatus.timeRecording = false;
        }

        if (
            (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) &&
            this.configService.showEquipment(notificationModel.taskGroup)
        ) {
            const equipmentStatusInArrival = notificationModel.isCentriCareContract()
                ? this.valueHelperService.getCentriCareStatInArrival()
                : this.valueHelperService.getEquipmentStatusInArrival();
            const equipmentStatusAfterFix = notificationModel.isCentriCareContract()
                ? this.valueHelperService.getCentriCareStatusAfterFix()
                : this.valueHelperService.getEquipmentStatusAfterFix();
            const equipmentSalesInfo = this.valueHelperService.getEquipmentBKInfo();
            const equipmentCommercialInfoDefaultCode = this.valueHelperService.getCommercialInfoDefaultCode();
            const equipmentSRSVPNConnection = this.valueHelperService.getSRSVPNConnection();

            let equipmentErrorCodes: ValueHelperModel[] = [];
            // logic for selection value helpers is different between P58 and NGMAA
            if (this.clientManager.isNgmP58()) {
                equipmentErrorCodes = this.valueHelperService.getEquipmentErrorCodeP58(notificationModel.taskGroup);
            }
            if (this.clientManager.isNgmAP0()) {
                equipmentErrorCodes = this.valueHelperService.getEquipmentErrorCodeAA(notificationModel.orderType);
            }
            const equipmentReasonsCodes = this.valueHelperService.getEquipmentReason();

            this.equipment = new ConfirmationEquipmentModel(
                notificationModel,
                this.timeRecording.timeRecordingEntries.value,
                equipmentStatusInArrival,
                equipmentStatusAfterFix,
                equipmentErrorCodes,
                equipmentSalesInfo,
                equipmentCommercialInfoDefaultCode,
                equipmentReasonsCodes,
                equipmentSRSVPNConnection,
            );
            this.accordionOpenedStatus.equipment = false;
        }

        if (
            (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) &&
            this.configService.showMaterialConsumption(notificationModel.taskGroup)
        ) {
            if (type === CONFIRMATION_TYPES.SHORT) {
                const pickedMaterials = this.materialService.getAllPickedMaterialsForNotification(
                    notificationModel.identifier,
                );
                this.materials =
                    pickedMaterials.length > 0 ? new ConfirmationMaterialConsumeModel(notificationModel) : undefined;
                this.accordionOpenedStatus.materials = pickedMaterials.length === 0;
            } else {
                const canUserAddMaterials =
                    this.configService.getDisplayAddMaterialButton() ||
                    notificationModel.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;
                const localMaterials = this.materialService.getAllMaterialsForNotification(
                    notificationModel.identifier,
                );
                this.materials = new ConfirmationMaterialConsumeModel(notificationModel);
                this.accordionOpenedStatus.materials = !canUserAddMaterials && localMaterials.length === 0;
            }
        }

        if (
            (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) &&
            this.configService.showMeasurementTools(notificationModel.taskGroup) &&
            type !== CONFIRMATION_TYPES.SHORT &&
            !this.isNotificationRemoteWork
        ) {
            this.measurementTool = new ConfirmationMeasurementToolsModel(
                notificationModel.notificationType,
                notificationModel.processIndicator,
                notificationModel.isApplicationSpecialist,
                this.equipment ? this.equipment.equipments.selectedEntry.model : undefined,
                [],
                notificationModel,
            );
            this.accordionOpenedStatus.measurementTool = false;
        }

        const isMeasuringPointsAvailable = notificationModel.equipmentSet.some(
            (equipment) => equipment.measuringPoint.value,
        );
        if (
            (this.clientManager.isNgmAA() || this.clientManager.isNgmP58()) &&
            isMeasuringPointsAvailable &&
            this.configService.showMeasuringPoints(notificationModel.taskGroup) &&
            type !== CONFIRMATION_TYPES.SHORT
        ) {
            this.measuringPoints = new ConfirmationMeasuringPointsModel(notificationModel.equipmentSet);
            this.accordionOpenedStatus.measuringPoints = false;
        }

        if (this.clientManager.isNgmP58OrSA()) {
            const displayDistance =
                this.clientManager.isNgmSA() ||
                (this.configService.getDisplayConfirmationDistance() && !this.isNotificationRemoteWork);
            const displayExpenses =
                !this.userMasterService.isPaceOnlineUser() &&
                !this.clientManager.isNgmSA() &&
                !this.isNotificationRemoteWork &&
                !this.configService.isTimesheetDisabled() &&
                this.configService.getDisplayConfirmationExpenses();
            const displayMileageTravel = this.configService.getUseMileageHistory();
            const displayAdditionalTravelCost =
                this.configService.getDisplayAdditionalTravelCost() && !this.isNotificationRemoteWork;
            if (displayDistance || displayExpenses || displayMileageTravel || displayAdditionalTravelCost) {
                this.expenses = new ConfirmationExpensesModel(
                    notificationIdentifier,
                    this.isNotificationRemoteWork,
                    '',
                    false,
                );
                this.accordionOpenedStatus.expenses = false;
            }
        }

        if ((this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) && type !== CONFIRMATION_TYPES.SHORT) {
            this.serviceReport = new ConfirmationServiceReportModel(notificationModel);
            this.accordionOpenedStatus.serviceReport = false;
        }

        if (
            type !== CONFIRMATION_TYPES.SHORT &&
            this.configService.getShowUploadToDocBoxForNotificationType(notificationModel.notificationType) &&
            this.valueHelperService.getUploadToDocboxValueHelpers().length > 0
        ) {
            this.uploadToDocBox = new ConfirmationUploadToDocboxModel([], notificationModel);
            this.accordionOpenedStatus.uploadToDocBox = false;
        }

        if (this.configService.showParticipantList(notificationModel.taskGroup) && type !== CONFIRMATION_TYPES.SHORT) {
            this.participants = new ConfirmationParticipantsModel(_.cloneDeep(notificationModel.participantSet));
            this.accordionOpenedStatus.participants = false;
        }

        if (this.configService.isSourceReplacementEnabled() && notificationModel.sourceReplacements?.length > 0) {
            this.sourceReplacements = new ConfirmationSourceReplacementsModel(
                notificationIdentifier,
                notificationModel.sourceReplacements,
            );
            this.accordionOpenedStatus.sourceReplacements = false;
        }

        if (
            this.clientManager.isNgmP58() &&
            this.configService.getComplaintNotificationType(notificationModel.notificationType)
        ) {
            this.complaints = new ConfirmationComplaintsP58Model(notificationIdentifier, notificationModel.userStatus);
            this.accordionOpenedStatus.complaints = false;
        }

        if (
            (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) &&
            this.configService.showFollowup(notificationModel.taskGroup)
        ) {
            this.followup = new ConfirmationFollowupModel(
                notificationModel,
                notificationModel.notificationType,
                type,
                this.materialService.isMaterialRequestedForcesFollowup(notificationModel.identifier),
                this.equipment ? this.equipment.getEquipmentStatus() : undefined,
                this.materials ? this.materials.listMaterial.value : undefined,
            );
            this.accordionOpenedStatus.followup = false;
        }

        if (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) {
            this.utcDiff = <DurationModel>this.set(notificationModel.timeZone.utcDiff);
        }

        if (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) {
            this.utcSign = <TextModel>this.set(notificationModel.timeZone.utcSign);
        }

        if (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) {
            this.timeZone = <TextModel>this.set(notificationModel.timeZone.timeZone);
        }

        if (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) {
            this.jobRole = <TextModel>this.set(new TextModel('JobRole', notificationModel.taskGroup));
        }

        if (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) {
            this.shortConfirmationIdent = <TextModel>(
                this.set(new TextModel('ShortConfirmationIdent', type === CONFIRMATION_TYPES.SHORT ? 'X' : ''))
            );
        }
        if (this.clientManager.isNgmAP0()) {
            this.userMasterService.setSalesOrgAndPlantForModel(this);
            // Add confirmation submission time which can display local time on service report when the user confirmed notification
            this.submissionDateTime = <TextModel>(
                this.set(new TextModel('LocalConfirmationTime', moment().format(ConfigConstants.formatDateNoTimezone)))
            );
        }

        if (this.clientManager.isPaceUS()) {
            this.attendees = new ConfirmationAttendeesModel();
            this.accordionOpenedStatus.attendees = false;
        }

        if (this.clientManager.isPaceUS()) {
            this.interruptions = new ConfirmationInterruptionsModel();
            this.accordionOpenedStatus.interruptions = false;
        }

        if (this.clientManager.isPaceUS()) {
            this.additionalEmails = new ConfirmationAdditionalEmailsModel();
            this.accordionOpenedStatus.additionalEmails = false;
        }

        if (this.clientManager.isPaceUS()) {
            this.eventSummary = new ConfirmationEventSummaryModel();
            this.accordionOpenedStatus.eventSummary = false;
        }

        if (this.clientManager.isPaceUS()) {
            const mainEquipment = notificationModel.getMainEquipment();
            if (!mainEquipment) {
                throw new Error(
                    `ConfirmationModel: Encountered notification ${notificationModel.identifier.toString()} without main equipment! This is not supported!`,
                );
            }

            this.timeRecordingP40 = new ConfirmationTimeRecordingP40Model(
                notificationModel.deliveryType,
                mainEquipment.division.value,
            );
            this.accordionOpenedStatus.timeRecordingP40 = false;
        }

        if (this.clientManager.isPaceUS()) {
            this.reportText = new ConfirmationReportTextModel();
            this.accordionOpenedStatus.reportText = false;
        }

        if (this.clientManager.isPaceUS()) {
            this.commentsForCustomer = new ConfirmationCommentsForCustomerModel();
            this.accordionOpenedStatus.commentsForCustomer = false;
        }

        if (this.clientManager.isPaceUS()) {
            this.trainingEventCompleted = new ConfirmationTrainingEventCompletedModel();
            this.accordionOpenedStatus.trainingEventCompleted = false;
        }

        if (notificationModel.isPaceFormsEnabled && this.configService.getDocMissingProtocolsEnabled()) {
            this.reasonMissingProtocols = new ConfirmationReasonMissingProtocolModel(
                notificationModel.notificationType,
            );
        }

        this.isSubmitted = false;
        this.saveTimestamp = 0;
    }

    public toJSON(): Partial<ConfirmationModel> {
        return {
            complaints: this.complaints,
            equipment: this.equipment,
            expenses: this.expenses,
            followup: this.followup,
            measurementTool: this.measurementTool,
            measuringPoints: this.measuringPoints,
            notificationIdentifier: this.notificationIdentifier,
            uploadToDocBox: this.uploadToDocBox,
            participants: this.participants,
            sourceReplacements: this.sourceReplacements,
            serviceReport: this.serviceReport,
            timeRecording: this.timeRecording,
            timeRecordingP40: this.timeRecordingP40,
            attendees: this.attendees,
            interruptions: this.interruptions,
            additionalEmails: this.additionalEmails,
            eventSummary: this.eventSummary,
            reportText: this.reportText,
            commentsForCustomer: this.commentsForCustomer,
            trainingEventCompleted: this.trainingEventCompleted,
            accordionOpenedStatus: {
                equipment: this.accordionOpenedStatus.equipment,
                complaints: this.accordionOpenedStatus.complaints,
                complaintsAA: this.accordionOpenedStatus.complaintsAA,
                materials: this.accordionOpenedStatus.materials,
                measurementTool: this.accordionOpenedStatus.measurementTool,
                measuringPoints: this.accordionOpenedStatus.measuringPoints,
                serviceReport: this.accordionOpenedStatus.serviceReport,
                timeRecording: this.accordionOpenedStatus.timeRecording,
                expenses: this.accordionOpenedStatus.expenses,
                uploadToDocBox: this.accordionOpenedStatus.uploadToDocBox,
                participants: this.accordionOpenedStatus.participants,
                sourceReplacements: this.accordionOpenedStatus.sourceReplacements,
                followup: this.accordionOpenedStatus.followup,
                timeRecordingP40: this.accordionOpenedStatus.timeRecordingP40,
                attendees: this.accordionOpenedStatus.attendees,
                interruptions: this.accordionOpenedStatus.interruptions,
                additionalEmails: this.accordionOpenedStatus.additionalEmails,
                eventSummary: this.accordionOpenedStatus.eventSummary,
                reportText: this.accordionOpenedStatus.reportText,
                commentsForCustomer: this.accordionOpenedStatus.commentsForCustomer,
                trainingEventCompleted: this.accordionOpenedStatus.trainingEventCompleted,
            },
            saveTimestamp: this.saveTimestamp,
        };
    }

    public getOutModel(): ODataConfirmationOutModel {
        const outModel = <ODataConfirmationOutModel>super.getOutModel();

        // we need to use lodash mergeWith here because we want to not overwrite any sets
        // (eg. CrmCodesSet (equipment and time recording are adding values to this set))
        _.mergeWith(outModel, this.notificationIdentifier.getOutModel());
        _.mergeWith(outModel, this.equipment ? this.equipment.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.materials ? this.materials.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.measurementTool ? this.measurementTool.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.serviceReport ? this.serviceReport.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.measuringPoints ? this.measuringPoints.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.participants ? this.participants.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.sourceReplacements ? this.sourceReplacements.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.uploadToDocBox ? this.uploadToDocBox.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.followup ? this.followup.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.expenses ? this.expenses.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.complaints ? this.complaints.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.timeRecording ? this.timeRecording.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.timeRecordingP40 ? this.timeRecordingP40.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.attendees ? this.attendees.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.interruptions ? this.interruptions.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.additionalEmails ? this.additionalEmails.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.eventSummary ? this.eventSummary.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.reportText ? this.reportText.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.commentsForCustomer ? this.commentsForCustomer.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(
            outModel,
            this.trainingEventCompleted ? this.trainingEventCompleted.getOutModel() : {},
            mergeCustomizer,
        );
        // no summary for PACE US
        _.mergeWith(outModel, this.summary ? this.summary.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(
            outModel,
            this.reasonMissingProtocols ? this.reasonMissingProtocols.getOutModel() : {},
            mergeCustomizer,
        );

        return outModel;
    }
}

export class ConfirmationModelFromJson extends ConfirmationModel {
    public constructor(data: JsonConfirmationModel, type: CONFIRMATION_TYPES) {
        super(new NotificationIdentifierModelFromJson(data.notificationIdentifier), type);
        const notificationModel = this.notificationService.getNotificationByIdentifier(
            new NotificationIdentifierModelFromJson(data.notificationIdentifier),
        );

        if (!notificationModel) {
            throw new Error(
                `Could not load notification ${this.notificationIdentifier} for confirmation model from json!`,
            );
        }

        const equipmentStatusInArrival = notificationModel.isCentriCareContract()
            ? this.valueHelperService.getCentriCareStatInArrival()
            : this.valueHelperService.getEquipmentStatusInArrival();
        const equipmentStatusAfterFix = notificationModel.isCentriCareContract()
            ? this.valueHelperService.getCentriCareStatusAfterFix()
            : this.valueHelperService.getEquipmentStatusAfterFix();
        const equipmentSalesInfo = this.valueHelperService.getEquipmentBKInfo();
        const equipmentCommercialInfoDefaultCode = this.valueHelperService.getCommercialInfoDefaultCode();
        const equipmentSRSVPNConnection = this.valueHelperService.getSRSVPNConnection();

        let equipmentErrorCodes: ValueHelperModel[] = [];
        // logic for selection value helpers is different between P58 and NGMAA
        if (this.clientManager.isNgmP58()) {
            equipmentErrorCodes = this.valueHelperService.getEquipmentErrorCodeP58(notificationModel.taskGroup);
        }
        if (this.clientManager.isNgmAP0()) {
            equipmentErrorCodes = this.valueHelperService.getEquipmentErrorCodeAA(notificationModel.orderType);
        }

        const equipmentReasonsCodes = this.valueHelperService.getEquipmentReason();
        const timeRecordingBillableCodes = this.valueHelperService.getTimeRecordingBillableCodes();
        const statusModel = notificationModel.updatedStatus;
        const timeRecordingTaskTypeInfoCodes = this.clientManager.isNgmAP0()
            ? this.valueHelperService.getCustomTimeRecordingTasksForAP0(notificationModel)
            : this.configService.getEnableRemoteWork()
              ? this.valueHelperService.getRemoteWorkTimeRecordingTaskTypeInfo(statusModel)
              : this.valueHelperService.getTimeRecordingTaskTypeInfo();

        const timeRecordingDestinationCodes = this.valueHelperService.getTravelDestination();
        const timeRecordingMeansOfTransportationCodes = this.valueHelperService.getMeansOfTransportation();
        const phoneFixIndicatorAA =
            this.clientManager.isNgmAA() &&
            notificationModel.notificationType === ConfigConstants.NotificationTypes.Service &&
            this.valueHelperService.getPhoneFixIndicator();

        const timeRecording = data.timeRecording
            ? new ConfirmationTimeRecordingModelFromJson(
                  data.timeRecording,
                  notificationModel,
                  timeRecordingBillableCodes,
                  timeRecordingTaskTypeInfoCodes,
                  timeRecordingMeansOfTransportationCodes,
                  timeRecordingDestinationCodes,
                  phoneFixIndicatorAA,
              )
            : undefined;
        if (timeRecording) {
            this.timeRecording = timeRecording;
            this.accordionOpenedStatus.timeRecording = data.accordionOpenedStatus?.timeRecording ?? false;
        }

        const equipment = data.equipment
            ? new ConfirmationEquipmentModelFromJson(
                  data.equipment,
                  notificationModel,
                  timeRecording ? this.timeRecording.timeRecordingEntries.value : [],
                  equipmentStatusInArrival,
                  equipmentStatusAfterFix,
                  equipmentErrorCodes,
                  equipmentSalesInfo,
                  equipmentCommercialInfoDefaultCode,
                  equipmentReasonsCodes,
                  equipmentSRSVPNConnection,
              )
            : undefined;
        if (equipment) {
            this.equipment = equipment;
            this.accordionOpenedStatus.equipment = data.accordionOpenedStatus?.equipment ?? false;
        }

        if (
            (this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) &&
            this.configService.showMaterialConsumption(notificationModel.taskGroup)
        ) {
            // special case for material as we do not load confirmation back from file service
            // so always a material model use to be created without checking condition if material has to be shown or no
            const pickedMaterials = this.materialService.getAllPickedMaterialsForNotification(
                notificationModel.identifier,
            );
            if (
                type !== CONFIRMATION_TYPES.SHORT ||
                (type === CONFIRMATION_TYPES.SHORT && pickedMaterials.length > 0)
            ) {
                this.materials = new ConfirmationMaterialConsumeModel(notificationModel);
                this.accordionOpenedStatus.materials = data.accordionOpenedStatus?.materials ?? false;
            }
        }
        // this check is necessary to hide the sections in the short confirmation when going
        // from a saved full confirmation to a short confirmation
        if ((this.clientManager.isNgmAP0() || this.clientManager.isNgmP58()) && type !== CONFIRMATION_TYPES.SHORT) {
            const measurementTool =
                data.measurementTool && !this.isNotificationRemoteWork
                    ? new ConfirmationMeasurementToolsModelFromJson(
                          notificationModel.notificationType,
                          notificationModel.processIndicator,
                          notificationModel.isApplicationSpecialist,
                          this.equipment ? this.equipment.equipments.selectedEntry.model : undefined,
                          data.measurementTool,
                          notificationModel,
                      )
                    : undefined;
            if (measurementTool) {
                this.measurementTool = measurementTool;
                this.accordionOpenedStatus.measurementTool = data.accordionOpenedStatus?.measurementTool ?? false;
            }

            const measuringPoints = data.measuringPoints
                ? new ConfirmationMeasuringPointsModelFromJson(data.measuringPoints, notificationModel.equipmentSet)
                : undefined;
            // Migration logic starts
            // it is possible that measuringPoints model is saved in local storage even when there are no
            // measuring points to be displayed, hence it is checked again
            const isMeasuringPointsAvailable = notificationModel.equipmentSet.some((e) => e.measuringPoint.value);
            // Migration logic ends
            if (measuringPoints && isMeasuringPointsAvailable) {
                this.measuringPoints = measuringPoints;
                this.accordionOpenedStatus.measuringPoints = data.accordionOpenedStatus?.measuringPoints ?? false;
            }

            const serviceReport = data.serviceReport
                ? new ConfirmationServiceReportModelFromJson(data.serviceReport, notificationModel)
                : undefined;
            if (serviceReport) {
                this.serviceReport = serviceReport;
                this.accordionOpenedStatus.serviceReport = data.accordionOpenedStatus?.serviceReport ?? false;
            }

            const uploadToDocBox = data.uploadToDocBox
                ? new ConfirmationUploadToDocboxModelFromJson(data.uploadToDocBox, notificationModel)
                : undefined;
            if (uploadToDocBox) {
                this.uploadToDocBox = uploadToDocBox;
                this.accordionOpenedStatus.uploadToDocBox = data.accordionOpenedStatus?.uploadToDocBox ?? false;
            }

            const participants = data.participants
                ? new ConfirmationParticipantsModelFromJson(data.participants)
                : undefined;
            if (participants) {
                this.participants = participants;
                this.accordionOpenedStatus.participants = data.accordionOpenedStatus?.participants ?? false;
            }

            const sourceReplacements = data.sourceReplacements
                ? new ConfirmationSourceReplacementsModelFromJson(data.sourceReplacements, notificationModel.identifier)
                : undefined;
            if (sourceReplacements) {
                this.sourceReplacements = sourceReplacements;
                this.accordionOpenedStatus.sourceReplacements = data.accordionOpenedStatus?.sourceReplacements ?? false;
            }
        }

        const followup = data.followup
            ? new ConfirmationFollowupModelFromJson(
                  data.followup,
                  notificationModel,
                  notificationModel.notificationType,
                  type,
                  this.materialService.isMaterialRequestedForcesFollowup(notificationModel.identifier),
                  equipment ? equipment.getEquipmentStatus() : undefined,
                  this.materials ? this.materials.listMaterial.value : undefined,
                  this.configService.getDisplayTrendCodeInFollowup() && data.equipment ? data.equipment : undefined, // migr ation logic SHP-3884
              )
            : undefined;
        if (followup) {
            this.followup = followup;
            this.accordionOpenedStatus.followup = data.accordionOpenedStatus?.followup ?? false;
        }

        const expenses = data.expenses
            ? new ConfirmationExpensesModelFromJson(data.expenses, this.isNotificationRemoteWork)
            : undefined;
        if (expenses) {
            this.expenses = expenses;
            this.accordionOpenedStatus.expenses = data.accordionOpenedStatus?.expenses ?? false;
        }

        const complaints = data.complaints
            ? new ConfirmationComplaintsModelFromJson(
                  data.complaints,
                  notificationModel.identifier,
                  notificationModel.userStatus,
              )
            : undefined;
        if (complaints) {
            this.complaints = complaints;
            this.accordionOpenedStatus.complaints = data.accordionOpenedStatus?.complaints ?? false;
        }

        const interruptions = data.interruptions
            ? new ConfirmationInterruptionsModelFromJson(data.interruptions)
            : undefined;
        if (interruptions) {
            this.interruptions = interruptions;
            this.accordionOpenedStatus.interruptions = data.accordionOpenedStatus?.interruptions ?? false;
        }

        if (data.attendees) {
            this.attendees = new ConfirmationAttendeesModelFromJson(data.attendees);
            this.accordionOpenedStatus.attendees = data.accordionOpenedStatus?.attendees ?? false;
        }

        if (data.additionalEmails) {
            this.additionalEmails = new ConfirmationAdditionalEmailsModelFromJson(data.additionalEmails);
            this.accordionOpenedStatus.additionalEmails = data.accordionOpenedStatus?.additionalEmails ?? false;
        }

        if (data.eventSummary) {
            this.eventSummary = new ConfirmationEventSummaryModelFromJson(data.eventSummary);
            this.accordionOpenedStatus.eventSummary = data.accordionOpenedStatus?.eventSummary ?? false;
        }

        if (data.interruptions) {
            this.interruptions = new ConfirmationInterruptionsModelFromJson(data.interruptions);
            this.accordionOpenedStatus.interruptions = data.accordionOpenedStatus?.interruptions ?? false;
        }

        if (data.timeRecordingP40) {
            this.timeRecordingP40 = new ConfirmationTimeRecordingP40ModelFromJson(data.timeRecordingP40);
            this.accordionOpenedStatus.timeRecordingP40 = data.accordionOpenedStatus?.timeRecordingP40 ?? false;
        }

        if (data.reportText) {
            this.reportText = new ConfirmationReportTextModelFromJson(data.reportText);
            this.accordionOpenedStatus.reportText = data.accordionOpenedStatus?.reportText ?? false;
        }

        if (data.additionalEmails) {
            this.additionalEmails = new ConfirmationAdditionalEmailsModelFromJson(data.additionalEmails);
            this.accordionOpenedStatus.additionalEmails = data.accordionOpenedStatus?.additionalEmails ?? false;
        }

        if (data.commentsForCustomer) {
            this.commentsForCustomer = new ConfirmationCommentsForCustomerModelFromJson(data.commentsForCustomer);
            this.accordionOpenedStatus.attendees = data.accordionOpenedStatus?.attendees ?? false;
        }

        if (data.trainingEventCompleted) {
            this.trainingEventCompleted = new ConfirmationTrainingEventCompletedModelFromJson(
                data.trainingEventCompleted,
            );
            this.accordionOpenedStatus.trainingEventCompleted =
                data.accordionOpenedStatus?.trainingEventCompleted ?? false;
        }

        this.saveTimestamp = data.saveTimestamp ?? 0;
    }
}

export interface JsonConfirmationModel {
    notificationIdentifier: JsonNotificationIdentifierModel;
    equipment: JsonConfirmationEquipmentModel;
    measurementTool: JsonConfirmationMeasurementToolsModel;
    measuringPoints: JsonConfirmationMeasuringPointsModel;
    serviceReport: JsonConfirmationServiceReportModel;
    uploadToDocBox: JsonConfirmationUploadToDocboxModel;
    participants: JsonConfirmationParticipantsModel;
    sourceReplacements: JsonConfirmationSourceReplacementsModel;
    expenses: JsonConfirmationExpensesModel;
    complaints: JsonConfirmationComplaintsModel;
    followup: JsonConfirmationFollowupModel;
    interruptions: JsonConfirmationInterruptionsModel;
    timeRecording: JsonConfirmationTimeRecordingModel;
    timeRecordingP40: JsonConfirmationTimeRecordingP40Model;
    attendees: JsonConfirmationAttendeesModel;
    additionalEmails: JsonConfirmationAdditionalEmailsModel;
    eventSummary: JsonConfirmationEventSummaryModel;
    reportText: JsonConfirmationReportTextModel;
    commentsForCustomer: JsonConfirmationCommentsForCustomerModel;
    trainingEventCompleted: JsonConfirmationTrainingEventCompletedModel;
    type: CONFIRMATION_TYPES;
    accordionOpenedStatus: {
        equipment: boolean;
        complaints: boolean;
        complaintsAA: boolean;
        materials: boolean;
        measurementTool: boolean;
        measuringPoints: boolean;
        serviceReport: boolean;
        timeRecording: boolean;
        expenses: boolean;
        uploadToDocBox: boolean;
        participants: boolean;
        sourceReplacements: boolean;
        followup: boolean;
        timeRecordingP40: boolean;
        attendees: boolean;
        interruptions: boolean;
        additionalEmails: boolean;
        eventSummary: boolean;
        reportText: boolean;
        commentsForCustomer: boolean;
        trainingEventCompleted: boolean;
    };
    saveTimestamp: number;
}

export interface ODataConfirmationOutModel
    extends ODataNotificationIdentifierOutModel,
        ODataConfirmationEquipmentOutModel,
        ODataConfirmationFollowupOutModel,
        ODataConfirmationSummaryOutModel,
        ODataConfirmationExpensesOutModel,
        ODataConfirmationTimeRecordingOutModel,
        ODataConfirmationMaterialConsumeOutModel,
        ODataConfirmationComplaintsOutModel,
        ODataConfirmationMeasurementToolsOutModel,
        ODataConfirmationMeasuringPointsOutModel,
        ODataConfirmationParticipantsOutModel,
        ODataConfirmationServiceReportOutModel,
        ODataTimeZoneOutModel,
        ODataObject {
    JobRole: string;
    ShortConfirmationIdent: string;
    SalesOrg: string;
    Plant: string;
}

interface AccordionOpenedStatus {
    [key: string]: boolean; // Index signature
    equipment: boolean;
    complaints: boolean;
    materials: boolean;
    measurementTool: boolean;
    measuringPoints: boolean;
    serviceReport: boolean;
    timeRecording: boolean;
    expenses: boolean;
    uploadToDocBox: boolean;
    participants: boolean;
    sourceReplacements: boolean;
    followup: boolean;
    timeRecordingP40: boolean;
    attendees: boolean;
    interruptions: boolean;
    additionalEmails: boolean;
    eventSummary: boolean;
    reportText: boolean;
    commentsForCustomer: boolean;
    trainingEventCompleted: boolean;
}
